export type CategoriaInconsistencia = 'Altas' | 'Moderadas'

/**
 * Abas do modal, na ordem exibida: Altas | Moderadas | Completo.
 * 'Completo' não é uma categoria: é a visão do texto CRU do laboratório, como a
 * produção exibe. 'Altas'/'Moderadas' são as tabelas estruturadas.
 */
export type AbaInconsistencia = CategoriaInconsistencia | 'Completo'

/** Uma inconsistência que o parser conseguiu estruturar em colunas. */
export type SoilInfo = {
  tipo: CategoriaInconsistencia
  elemento: string
  /** 'maximo' = valor acima do teto; 'minimo' = valor abaixo do piso. */
  limite_tipo: 'maximo' | 'minimo'
  valor: number
  /** Limite rompido — teto ou piso, conforme `limite_tipo`. `null` se a frase não o trouxe. */
  limite: number | null
  ponto: number | null
  profundidade: string | null
  /** Linha original, para o usuário conferir o texto exato do laboratório. */
  original: string
}

export interface RelatorioInconsistencias {
  /** Linhas reconhecidas, exibidas na tabela. */
  itens: SoilInfo[]
  /** Linhas que o parser NÃO entendeu, por categoria — exibidas como texto cru. */
  nao_interpretadas: Record<CategoriaInconsistencia, string[]>
  /**
   * Todas as linhas do texto original, incluindo os cabeçalhos de categoria e
   * sem nenhuma interpretação. Alimenta a aba "Completo", que espelha o que a
   * produção mostra.
   */
  linhas_originais: string[]
}

//#region REGEX
// Montadas por partes e por ÂNCORAS INDEPENDENTES: em vez de exigir a frase
// inteira num único padrão (que quebra a cada mudança de redação do backend),
// cada campo é procurado por conta própria. Assim, texto novo ENTRE as âncoras
// não impede o reconhecimento, e um campo ausente vira `null` em vez de
// descartar a linha toda.

/** Número com vírgula ou ponto decimal — o backend mistura os dois na mesma linha. */
const NUM = '\\d+(?:[.,]\\d+)?'
/** Aceita máximo/maximo/máxima/maxima — acento e gênero variam entre mensagens. */
const MAX = 'm[áa]xim[oa]'
const MIN = 'm[íi]nim[oa]'

/** "Valor mínimo de k (0,04)" -> tipo do limite, elemento e valor medido. */
const R_CABECALHO = new RegExp(
  `valor\\s+(${MAX}|${MIN})\\s+(?:de|do|da)?\\s*([^()]+?)\\s*\\((${NUM})\\)`,
  'i'
)
/** "(mínimo de 0.05)" ou "(máximo: 50)" -> o limite rompido. */
const R_LIMITE = new RegExp(`\\(\\s*(?:${MAX}|${MIN})\\s*(?:de|:)?\\s*(${NUM})\\s*\\)`, 'i')
/** "para o ponto 5", "no ponto nº 7", "ponto 4". */
const R_PONTO = /pontos?\s*(?:n[º°.]?\s*)?(\d+)/i
/** "00-20", "20 - 40" — com ou sem a preposição "em" antes. */
const R_PROFUNDIDADE = /(\d{1,3}\s*-\s*\d{1,3})/

/** Cabeçalhos de seção que trocam a categoria corrente. */
const R_ALTAS = /^\s*altas\s*:?\s*$|altas\s*:/i
const R_MODERADAS = /^\s*moderadas\s*:?\s*$|moderadas\s*:/i
//#endregion

/** Converte "0,04" ou "0.05" em number. */
function paraNumero(bruto: string): number {
  return parseFloat(bruto.replace(',', '.'))
}

/** `true` para as linhas "Altas:" / "Moderadas:", que separam os blocos do texto. */
export function ehCabecalhoCategoria(linha: string): boolean {
  const limpa = (linha ?? '').replace(/<[^>]*>/g, '').trim()

  return R_ALTAS.test(limpa) || R_MODERADAS.test(limpa)
}

/**
 * Estrutura a observação do laboratório em linhas de tabela.
 *
 * O que a regex não reconhece NÃO é descartado: volta em `nao_interpretadas`
 * para ser exibido como texto, espelhando o comportamento da produção (que
 * imprime a observação crua via `dangerouslySetInnerHTML`). Assim uma mudança
 * na frase do backend degrada a exibição, mas nunca esconde a inconsistência.
 */
export function parseInconsistencias(textoBruto: string): RelatorioInconsistencias {
  const itens: SoilInfo[] = []
  const nao_interpretadas: Record<CategoriaInconsistencia, string[]> = {
    Altas: [],
    Moderadas: [],
  }

  // Separa por <br> (formato atual) e também por quebra de linha real, caso o
  // backend mude o formato — sem isso o texto inteiro viraria uma única linha.
  // As tags restantes (negrito etc.) são preservadas aqui: a aba "Completo" as
  // renderiza; só a análise por regex trabalha sobre o texto limpo.
  const linhas_originais = (textoBruto ?? '')
    .split(/<br\s*\/?>|\r?\n/gi)
    .map((linha) => linha.trim())
    .filter(Boolean)

  let categoriaAtual: CategoriaInconsistencia = 'Altas'

  const linhas = linhas_originais.map((linha) => linha.replace(/<[^>]*>/g, '').trim())

  linhas.forEach((linha) => {
    // Linha que continha apenas tags — nada a interpretar nem a exibir.
    if (!linha) return

    if (R_ALTAS.test(linha)) {
      categoriaAtual = 'Altas'
      return
    }
    if (R_MODERADAS.test(linha)) {
      categoriaAtual = 'Moderadas'
      return
    }

    const cabecalho = linha.match(R_CABECALHO)

    // Sem o cabeçalho não há elemento nem valor — a linha não vira coluna.
    if (!cabecalho) {
      nao_interpretadas[categoriaAtual].push(linha)
      return
    }

    const limite = linha.match(R_LIMITE)
    const ponto = linha.match(R_PONTO)
    const profundidade = linha.match(R_PROFUNDIDADE)

    itens.push({
      tipo: categoriaAtual,
      limite_tipo: new RegExp(MAX, 'i').test(cabecalho[1]) ? 'maximo' : 'minimo',
      elemento: cabecalho[2].trim(),
      valor: paraNumero(cabecalho[3]),
      limite: limite ? paraNumero(limite[1]) : null,
      ponto: ponto ? parseInt(ponto[1]) : null,
      profundidade: profundidade ? profundidade[1].replace(/\s/g, '') : null,
      original: linha,
    })
  })

  return { itens, nao_interpretadas, linhas_originais }
}

/**
 * Formata para exibição, cortando o excesso de casas decimais que o backend
 * envia (ex: 0.0500000000000000028 -> "0,05"). Mantém até 4 casas.
 */
export function formatarNumero(valor: number | null): string {
  if (valor === null || !Number.isFinite(valor)) return '-'

  return Number(valor.toFixed(4)).toLocaleString('pt-BR', { maximumFractionDigits: 4 })
}
